/**
 * Returns the persistent user UUID that is shared with the analytics tracker.
 * Reads the cookie written by the tracking package (ba_user_uuid) so the lead
 * code and the tracking UUID are always the same value.
 *
 * Falls back to crypto.randomUUID() only when the cookie doesn't exist yet
 * (e.g. the page loaded before TrackingProvider initialised).
 */
export const getLeadCode = () => {
  const cookieName = 'ba_user_uuid';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${cookieName}=`);
  if (parts.length === 2) {
    const uuid = parts.pop().split(';').shift();
    if (uuid) return uuid;
  }

  // Fallback: generate a new UUID and write the cookie so future calls are
  // consistent within this session.
  const uuid =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });

  document.cookie = `${cookieName}=${uuid}; max-age=${365 * 24 * 60 * 60}; path=/; SameSite=Lax`;
  return uuid;
};

export const setTrackingData = (data) => {
  // Get existing tracking data to preserve start_url if it exists
  const existingData = getTrackingData();

  // Add full URL to the tracking data
  const trackingData = {
    ...data,
    full_url: window.location.href,
    // Preserve start_url if it already exists, otherwise set it to current URL
    start_url: existingData.start_url || window.location.href,
    // Preserve referrer if it already exists, otherwise capture current referrer
    referrer: existingData.referrer || document.referrer || null,
    referrer_domain: existingData.referrer_domain || (document.referrer ? new URL(document.referrer).hostname : null),
  };

  // Store in multiple places for redundancy
  setTrackingParamsToStorage('localStorage', trackingData);
  setTrackingParamsToStorage('sessionStorage', trackingData);

  // Also store in a more persistent way (cookie)
  try {
    setTrackingParamsToStorage('cookie', trackingData);
  } catch (e) {
    console.log('Could not set tracking cookie');
  }
};

export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

export const getTrackingData = () => {
  const isDefaultData = (data) => {
    try {
      const parsed = JSON.parse(data);
      return parsed.utm_source === 'direct' && parsed.utm_medium === 'none';
    } catch (e) {
      return true;
    }
  };

  let data = getTrackingParamsFromStorage('localStorage');

  if (data && isDefaultData(data)) {
    const sessionData = getTrackingParamsFromStorage('sessionStorage');
    if (sessionData && !isDefaultData(sessionData)) {
      data = sessionData;
    }
  }

  if (!data || isDefaultData(data)) {
    const cookies = document.cookie.split(';');
    const trackingCookie = cookies.find((c) => c.trim().startsWith('trackingParams='));
    if (trackingCookie) {
      const cookieData = trackingCookie.split('=')[1];
      if (!isDefaultData(cookieData)) {
        data = cookieData;
      }
    }
  }

  const storedParams = data ? JSON.parse(data) : {};

  // Get fresh Facebook cookies
  const facebookCookies = getFacebookCookies();
  const browserFbc = facebookCookies.fbc;
  const browserFbp = facebookCookies.fbp;

  return {
    ...storedParams,
    // Only use browser fbc if we don't have a stored one (from URL parameter)
    fbc: storedParams.fbc || browserFbc,
    fbp: browserFbp,
  };
};

export const getFacebookCookies = () => {
  return {
    fbc: getCookie('_fbc'),
    fbp: getCookie('_fbp'),
  };
};

export const getTikTokTracking = () => {
  const parseJson = (value) => {
    if (!value) return {};
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  };

  const localTracking = parseJson(getTrackingParamsFromStorage('localStorage'));
  const sessionTracking = parseJson(getTrackingParamsFromStorage('sessionStorage'));
  const ttclidFromUrl = new URLSearchParams(window.location.search).get('ttclid');

  return {
    ttclid: ttclidFromUrl || localTracking.ttclid || sessionTracking.ttclid || null,
    ttp: getCookie('_ttp'),
  };
};

export const getTrackingParamsFromStorage = (storage = 'localStorage') => {
  if (storage === 'localStorage') {
    return localStorage.getItem('trackingParams');
  } else if (storage === 'sessionStorage') {
    return sessionStorage.getItem('trackingParams');
  } else if (storage === 'cookie') {
    return getCookie('trackingParams');
  } else {
    return null;
  }
};

export const setTrackingParamsToStorage = (storage = 'localStorage', data) => {
  if (storage === 'localStorage') {
    localStorage.setItem('trackingParams', JSON.stringify(data));
  } else if (storage === 'sessionStorage') {
    sessionStorage.setItem('trackingParams', JSON.stringify(data));
  } else if (storage === 'cookie') {
    document.cookie = `trackingParams=${JSON.stringify(data)}; max-age=2592000; path=/`;
  }
};
